import { useState, useEffect, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { ChevronLeft, ChevronRight } from "lucide-react";
import i18n from "../i18n/i18n";
import { StackedMemoryGallery } from "./ui/MemoeryCard";


// First, import all images at the top of the file
import image1 from '../assets/images/memories/1.jpg';
import image2 from '../assets/images/memories/2.jpg';
import image3 from '../assets/images/memories/3.jpg';
import image4 from '../assets/images/memories/4.jpg';
import image5 from '../assets/images/memories/5.jpg';
import image6 from '../assets/images/memories/6.jpg';
import image7 from '../assets/images/memories/7.jpg';
import image8 from '../assets/images/memories/8.jpg';
import image9 from '../assets/images/memories/9.jpg';
import image10 from '../assets/images/memories/10.jpg';



interface Memory {
  id: number;
  image: string;


}

interface MemoryGalleryProps {
  onComplete?: () => void;
}

const MemoryGallery = ({ onComplete }: MemoryGalleryProps) => {
  const [showBottomMessage, setShowBottomMessage] = useState(false);
  const [displayedBottomTitle, setDisplayedBottomTitle] = useState("");
  const [displayedBottomBody, setDisplayedBottomBody] = useState("");

  // Timings aligned with MessageSection speeds
  const SHOW_MESSAGE_MS = 30000;
  const TITLE_SPEED = 60;
  const BODY_SPEED = 60;
  const HOLD_AFTER_TYPING = 6000;

  const bottomTitle = i18n.t('memory_title');
  const bottomBody = i18n.t('memory_body');

  const memories: Memory[] = [
  { id: 1, image: image1 },
  { id: 2, image: image2 },
  { id: 3, image: image3 },
  { id: 4, image: image4 },
  { id: 5, image: image5 },
  { id: 6, image: image6 },
  { id: 7, image: image7 },
  { id: 8, image: image8 },
  { id: 9, image: image9 },
  { id: 10, image: image10 }
];
  // Schedule showing bottom message
  useEffect(() => {
    if (!onComplete) return;
    
    const timer = setTimeout(() => {
      setShowBottomMessage(true);
    }, SHOW_MESSAGE_MS);

    return () => clearTimeout(timer);
  }, [onComplete]);

  // Typing animation + final advance after message displayed
  useEffect(() => {
    if (!showBottomMessage) return;

    // Type title
    if (displayedBottomTitle.length < bottomTitle.length) {
      const t = setTimeout(() => {
        setDisplayedBottomTitle(bottomTitle.slice(0, displayedBottomTitle.length + 1));
      }, TITLE_SPEED);
      return () => clearTimeout(t);
    }

    // Title done -> type body
    if (displayedBottomBody.length < bottomBody.length) {
      const t = setTimeout(() => {
        setDisplayedBottomBody(bottomBody.slice(0, displayedBottomBody.length + 1));
      }, BODY_SPEED);
      return () => clearTimeout(t);
    }

    // Both typed -> schedule onComplete after HOLD_AFTER_TYPING
    if (onComplete) {
      const timer = setTimeout(() => {
        onComplete();
      }, HOLD_AFTER_TYPING);
      return () => clearTimeout(timer);
    }
  }, [showBottomMessage, displayedBottomTitle, displayedBottomBody, onComplete, bottomTitle, bottomBody]);

  return (
    <section className="min-h-screen py-20 px-6 bg-gradient-to-b from-background to-background/50 relative">
      {/* Stacked Memory Gallery */}
      {!showBottomMessage && (
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-6xl font-bold text-center mb-16 bg-gradient-romantic bg-clip-text text-transparent animate-fade-in">
            {i18n.t('our_story_together')}
          </h2>
          
          <div className="min-h-[600px]">
            <StackedMemoryGallery 
              images={memories}
              className="h-full"
            />
          </div>
        </div>
      )}

      {/* Centered Bottom Message overlay */}
      {showBottomMessage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6">
          <div className="backdrop-blur-md rounded-3xl p-8 max-w-2xl w-full text-center shadow-2xl animate-fade-in">
            <p className="text-2xl md:text-3xl font-bold mb-4 text-foreground">
              {displayedBottomTitle}
              {displayedBottomTitle.length < bottomTitle.length && (
                <span className="animate-pulse">|</span>
              )}
            </p>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed whitespace-pre-wrap">
              {displayedBottomBody}
              {displayedBottomBody.length < bottomBody.length && (
                <span className="animate-pulse">|</span>
              )}
            </p>
          </div>
        </div>
      )}
    </section>
  );
};

export default MemoryGallery;